import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@mantine/core";
import VideoCall from "./VideoCall";
import apiService from "../../services/api";
import type { Pet, SessionDeliverables } from "../../types";

interface SessionRequestData {
  client_id: string;
  trainer_id?: string;
  duration_minutes?: number;
  session_started_at?: string;
  [key: string]: unknown;
}

interface _EndData {
  sessionId: string;
  duration: number;
  endTime: string;
  status: string;
}

const VideoCallPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestId = searchParams.get("request_id");

  const [sessionData, setSessionData] = useState<SessionRequestData | null>(null);
  const [petInfo, setPetInfo] = useState<Pet | null>(null);
  const [lastSessionInfo, setLastSessionInfo] = useState<SessionDeliverables | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [joinRegistered, setJoinRegistered] = useState<boolean>(false);
  const [sessionStartedAt, setSessionStartedAt] = useState<string | null>(null);

  const trainerId = localStorage.getItem("trainerId");
  const clientId = localStorage.getItem("clientId");

  useEffect(() => {
    if (!requestId) {
      setError("No session request ID provided");
      setLoading(false);
      return;
    }

    loadSessionData();
  }, [requestId]);

  useEffect(() => {
    const registerJoin = async (): Promise<void> => {
      try {
        const role = trainerId ? "trainer" : "client";
        await apiService.registerSessionJoin(requestId as string, role);
      } catch (err) {
        console.error("Failed to register session join:", err);
      } finally {
        setJoinRegistered(true);
      }
    };

    if (!loading && sessionData && requestId && !joinRegistered) {
      registerJoin();
    }
  }, [loading, sessionData, requestId, trainerId, joinRegistered]);

  const loadSessionData = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = (await apiService.getSessionRequestDetails(requestId as string)) as Record<
        string,
        unknown
      >;

      if (response.Code === 200) {
        setSessionData(response.request as SessionRequestData);
        setPetInfo(response.pet_info as Pet | null);
        setLastSessionInfo(response.last_session as SessionDeliverables | null);
      } else {
        setError((response.Message as string) || "Failed to load session data");
      }
    } catch (err) {
      console.error("Error loading session data:", err);
      setError("Failed to load session data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSessionStartTime = async (): Promise<void> => {
      if (!requestId || !joinRegistered) return;

      try {
        const role = trainerId ? "trainer" : "client";
        const joinResponse = (await apiService.registerSessionJoin(requestId, role)) as Record<
          string,
          unknown
        >;
        if (joinResponse.Code === 200 && joinResponse.session_started_at) {
          setSessionStartedAt(joinResponse.session_started_at as string);
        }
      } catch (err) {
        console.error("Failed to fetch session start time:", err);
      }
    };

    if (joinRegistered && requestId) {
      fetchSessionStartTime();
    }
  }, [requestId, joinRegistered, trainerId]);

  const handleSessionEnd = async (_endData: string | Record<string, unknown>): Promise<void> => {
    try {
      // End the session on backend
      await apiService.endSession(requestId as string);

      // If trainer, redirect to post-session form
      if (trainerId) {
        navigate(`/post-session?request_id=${requestId}`);
      } else {
        // If client, redirect to client portal
        navigate("/client-portal");
      }
    } catch (err) {
      console.error("Error ending session:", err);
      // Still redirect even if API call fails
      if (trainerId) {
        navigate(`/post-session?request_id=${requestId}`);
      } else {
        navigate("/client-portal");
      }
    }
  };

  const handleBackToPortal = (): void => {
    if (trainerId) {
      navigate("/trainer");
    } else {
      navigate("/client-portal");
    }
  };

  if (loading) {
    return (
      <div className="font-sans bg-background min-h-screen flex items-center justify-center text-foreground">
        <div className="text-center">
          <div className="text-3xl mb-5">🔗</div>
          <div className="text-muted-foreground">Loading session...</div>
        </div>
      </div>
    );
  }

  if (error || !sessionData) {
    return (
      <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5">
        <div className="bg-destructive/10 border border-destructive/25 rounded-xl p-8 text-center max-w-[500px]">
          <div className="text-3xl mb-5">❌</div>
          <h2 className="text-foreground mb-4">Error</h2>
          <p className="text-muted-foreground mb-5">{error || "Session not found"}</p>
          <Button onClick={handleBackToPortal} variant="outline" color="red" radius="md" size="md">
            Back to Portal
          </Button>
        </div>
      </div>
    );
  }

  return (
    <VideoCall
      sessionId={requestId as string}
      trainerId={trainerId || ""}
      clientId={clientId || sessionData.client_id}
      onSessionEnd={handleSessionEnd}
      onBackToClientPortal={handleBackToPortal}
      sessionOptions={{}}
      durationMinutes={sessionData.duration_minutes}
      petInfo={petInfo}
      lastSessionInfo={lastSessionInfo}
      sessionStartedAt={sessionStartedAt || sessionData.session_started_at || null}
    />
  );
};

export default VideoCallPage;
